import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Bot,
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  MessageSquareText,
  Plus,
  Search,
  Store,
  UserRound,
  X,
} from "lucide-react";
import { useState } from "react";
import {
  CLIENTES_POR_PAGINA,
  type Customer,
  customersPageQuery,
  MIN_BUSCA,
} from "@/api/customers";
import { establishmentQuery } from "@/api/establishment";
import { useAuth } from "@/auth/AuthProvider";
import { ClienteDialog } from "@/components/clientes/ClienteDialog";
import { initials, veioDoBot } from "@/components/clientes/ClienteFicha";
import { NovoAgendamentoDialog } from "@/components/NovoAgendamentoDialog";
import { PageActions } from "@/components/page-actions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDebounced } from "@/hooks/use-debounced";
import { formatPhoneBR, normalizePhone } from "@/lib/phone";

export const Route = createFileRoute("/_authed/clientes/")({
  staticData: {
    title: "Clientes",
    subtitle: "O bot cadastra sozinho quem chama no WhatsApp.",
  },
  component: ClientesPage,
});

/**
 * Arquivo `clientes.index.tsx`, não `clientes.tsx`: com o nome sem `.index`
 * esta rota virava a MÃE de `/clientes/$customerId`, e como ela não renderiza
 * `<Outlet />` a ficha do cliente casava a rota (o título do cabeçalho até
 * mudava) mas nunca aparecia.
 *
 * A tela é a LISTA (`GET /customers`), não uma busca por telefone exato: a
 * maior parte da base nasce sozinha, no primeiro contato do cliente com o bot,
 * e o dono não decora o número de ninguém. O campo de busca filtra a mesma
 * lista por nome, telefone ou id do WhatsApp — a API resolve qual é.
 */
function ClientesPage() {
  const { user } = useAuth();
  const establishmentId = user?.establishment_id ?? "";
  const { data: establishment } = useQuery({
    ...establishmentQuery(establishmentId),
    enabled: Boolean(establishmentId),
  });
  const timeZone = establishment?.timezone ?? "America/Sao_Paulo";

  const [termo, setTermo] = useState("");
  const [page, setPage] = useState(1);
  // 250ms: rápido o bastante pra parecer instantâneo, longo o bastante pra
  // uma palavra digitada inteira não virar seis requests.
  const termoAtrasado = useDebounced(termo, 250);

  const [cadastrando, setCadastrando] = useState(false);
  const [agendando, setAgendando] = useState<Customer | null>(null);

  const busca = termoAtrasado.trim();
  const curtoDemais = busca.length > 0 && busca.length < MIN_BUSCA;
  const lista = useQuery(customersPageQuery(termoAtrasado, page));

  const clientes = lista.data?.customers ?? [];
  const total = lista.data?.meta.total ?? 0;
  const ultimaPagina = Math.max(1, Math.ceil(total / CLIENTES_POR_PAGINA));
  // Telefone digitado que não achou ninguém vira atalho de cadastro — é o
  // caminho de quem atendeu uma ligação e está com o número na mão.
  const telefoneDigitado = normalizePhone(termo.trim());

  return (
    <>
      <PageActions>
        <Button size="sm" className="gap-1.5" onClick={() => setCadastrando(true)}>
          <Plus className="h-4 w-4" />
          Novo<span className="hidden sm:inline"> cliente</span>
        </Button>
      </PageActions>

      <div>
        <div className="mx-auto max-w-4xl p-4 sm:p-6">
          {/* Busca */}
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={termo}
              onChange={(e) => {
                setTermo(e.target.value);
                // Filtrar é começar de novo: manter a página 4 de um termo
                // antigo mostraria vazio num filtro que tem resultado.
                setPage(1);
              }}
              placeholder="Buscar por nome, telefone ou id do WhatsApp"
              aria-label="Buscar cliente"
              className="pl-9 pr-9"
            />
            {termo !== "" && (
              <button
                type="button"
                onClick={() => {
                  setTermo("");
                  setPage(1);
                }}
                aria-label="Limpar busca"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {curtoDemais && (
            <p className="mt-2 text-xs text-muted-foreground">
              Digite ao menos {MIN_BUSCA} caracteres para buscar.
            </p>
          )}

          {/* A contagem mora sob a busca, não no subtítulo do cabeçalho: ela
              muda a cada tecla, e o cabeçalho é onde ficam as coisas fixas da
              tela. Aqui ela também fica ao lado do campo que a alterou, que é
              pra onde o olho já está indo. */}
          {lista.isSuccess && !curtoDemais && (
            <p className="mt-2 text-xs text-muted-foreground">
              {busca
                ? `${total} ${total === 1 ? "resultado" : "resultados"} para "${busca}"`
                : `${total} ${total === 1 ? "cliente cadastrado" : "clientes cadastrados"}`}
            </p>
          )}

          {lista.isError && (
            <p role="alert" className="mt-4 text-sm text-destructive">
              Não foi possível carregar os clientes. Tente recarregar a página.
            </p>
          )}

          {lista.isPending && !curtoDemais && (
            <div className="mt-4 space-y-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          )}

          {lista.isSuccess && clientes.length === 0 && (
            <ListaVazia
              busca={busca}
              telefoneDigitado={telefoneDigitado}
              onCadastrar={() => setCadastrando(true)}
            />
          )}

          {clientes.length > 0 && (
            <>
              {/* Abaixo de `md` a tabela vira cartão: num telefone as colunas
                  de origem e data empurravam as ações pra fora da tela. */}
              <ul className="mt-4 space-y-3 md:hidden">
                {clientes.map((c) => (
                  <li key={c.id} className="space-y-3 rounded-md border bg-card p-4">
                    <Identificacao customer={c} />
                    <div className="flex flex-wrap items-center gap-2">
                      <OrigemBadge customer={c} />
                      <span className="text-xs text-muted-foreground">
                        desde {desde(c.created_at, timeZone)}
                      </span>
                      <Faltas customer={c} />
                    </div>
                    <Acoes customer={c} onAgendar={() => setAgendando(c)} />
                  </li>
                ))}
              </ul>

              <div className="mt-4 hidden rounded-md border bg-card md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Origem</TableHead>
                      <TableHead>Cliente desde</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clientes.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell>
                          <Identificacao customer={c} />
                          <Faltas customer={c} />
                        </TableCell>
                        <TableCell>
                          <OrigemBadge customer={c} />
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {desde(c.created_at, timeZone)}
                        </TableCell>
                        <TableCell>
                          <Acoes
                            customer={c}
                            onAgendar={() => setAgendando(c)}
                            alinharDireita
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}

          {ultimaPagina > 1 && (
            <div className="mt-4 flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                Página {page} de {ultimaPagina}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  disabled={page === 1 || lista.isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  disabled={page >= ultimaPagina || lista.isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ClienteDialog
        open={cadastrando}
        onOpenChange={setCadastrando}
        telefoneInicial={telefoneDigitado ? termo.trim() : undefined}
      />
      {/* Um dialog só, controlado: montar um por linha custaria uma árvore de
          formulário por cliente da página. */}
      <NovoAgendamentoDialog
        open={agendando !== null}
        onOpenChange={(aberto) => {
          if (!aberto) setAgendando(null);
        }}
        defaultCustomer={agendando}
        defaultClientName={agendando?.name}
      />
    </>
  );
}

/** "28/08/2026" no fuso do estabelecimento, nunca no do navegador. */
function desde(iso: string, timeZone: string) {
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone,
  }).format(new Date(iso));
}

function Identificacao({ customer }: { customer: Customer }) {
  return (
    <Link
      to="/clientes/$customerId"
      params={{ customerId: customer.id }}
      className="flex min-w-0 items-center gap-3 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold text-secondary-foreground">
        {customer.name ? initials(customer.name) : <UserRound className="h-4 w-4" />}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">
          {customer.name || "Sem nome"}
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {customer.whatsapp_phone
            ? formatPhoneBR(customer.whatsapp_phone)
            : "sem telefone · WhatsApp por id"}
        </span>
      </span>
    </Link>
  );
}

function OrigemBadge({ customer }: { customer: Customer }) {
  return veioDoBot(customer) ? (
    <Badge variant="secondary" className="gap-1 font-normal">
      <Bot className="h-3 w-3" />
      WhatsApp
    </Badge>
  ) : (
    <Badge variant="outline" className="gap-1 font-normal">
      <Store className="h-3 w-3" />
      Balcão
    </Badge>
  );
}

/**
 * Faltas seguidas. O badge só aparece a partir da depriorização (3 faltas), que
 * é quando muda alguma coisa de verdade — sinal obrigatório e menor prioridade
 * na fila. Abaixo disso é só um aviso discreto: uma falta acontece com
 * qualquer um, e carimbar cliente por isso na lista inteira é ruído.
 */
function Faltas({ customer }: { customer: Customer }) {
  if (customer.deprioritized_at) {
    return (
      <Badge variant="outline" className="gap-1 border-primary/40 text-primary">
        {customer.consecutive_no_shows} faltas seguidas
      </Badge>
    );
  }
  if (customer.consecutive_no_shows > 0) {
    return (
      <span className="text-xs text-muted-foreground">
        {customer.consecutive_no_shows}{" "}
        {customer.consecutive_no_shows === 1 ? "falta" : "faltas"} seguidas
      </span>
    );
  }
  return null;
}

function Acoes({
  customer,
  onAgendar,
  alinharDireita,
}: {
  customer: Customer;
  onAgendar: () => void;
  alinharDireita?: boolean;
}) {
  return (
    <div className={`flex gap-1 ${alinharDireita ? "justify-end" : ""}`}>
      <Button
        variant="ghost"
        size="sm"
        className="h-9 gap-1.5 px-2 text-xs sm:h-8"
        onClick={onAgendar}
      >
        <CalendarPlus className="h-3.5 w-3.5" />
        Agendar
      </Button>
      <Button
        asChild
        variant="ghost"
        size="sm"
        className="h-9 gap-1.5 px-2 text-xs sm:h-8"
      >
        <Link
          to="/clientes/$customerId"
          params={{ customerId: customer.id }}
          hash="conversas"
        >
          <MessageSquareText className="h-3.5 w-3.5" />
          Conversa
        </Link>
      </Button>
    </div>
  );
}

/**
 * Dois vazios diferentes, e confundi-los é o que faz o dono achar que perdeu a
 * base: "a busca não achou ninguém" é passageiro, "ainda não há cliente" é o
 * primeiro dia de uso — e aí o que ele precisa saber é que a lista se enche
 * sozinha quando o WhatsApp começar a receber mensagem.
 */
function ListaVazia({
  busca,
  telefoneDigitado,
  onCadastrar,
}: {
  busca: string;
  telefoneDigitado: string | null;
  onCadastrar: () => void;
}) {
  if (busca) {
    return (
      <div className="mt-4 rounded-md border border-dashed bg-card p-8 text-center">
        <p className="text-sm font-medium">Nenhum cliente para "{busca}".</p>
        <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
          A busca cobre nome, telefone e o id do WhatsApp, sem ligar pra acento nem
          pontuação. Confira o que digitou — ou cadastre este cliente.
        </p>
        <Button size="sm" className="mt-4 gap-1.5" onClick={onCadastrar}>
          <Plus className="h-4 w-4" />
          {telefoneDigitado ? `Cadastrar ${busca}` : "Novo cliente"}
        </Button>
      </div>
    );
  }
  return (
    <div className="mt-4 rounded-md border border-dashed bg-card p-8 text-center">
      <p className="text-sm font-medium">Nenhum cliente cadastrado ainda.</p>
      <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
        Esta lista se enche sozinha: todo mundo que mandar mensagem pro WhatsApp do
        estabelecimento vira um cliente aqui. Cadastre manualmente só quem chegou por
        telefone ou no balcão.
      </p>
      <Button size="sm" className="mt-4 gap-1.5" onClick={onCadastrar}>
        <Plus className="h-4 w-4" />
        Novo cliente
      </Button>
    </div>
  );
}
